import { IRequestEstimate, IRequestQuote } from '@core/interfaces/IRequest';
import FieldDimensionId from '@core/constants/FieldDimension';
import { MaterialId } from '@core/constants/Material';
import { getFormFieldDimensionFromShapeDimension } from '../transformers/Form';
import {
    IFormFieldDescription,
    IFormFieldContact,
    IFormFieldMaterial,
    IFormFieldDimension,
    IFormFieldAdditionalData,
    IFormFieldShape,
    IFormFieldKey,
    IFormFieldContactKey,
    IFormFieldShapeQuantities,
    IFormFieldBoolean,
    IFormFieldShapeDimension,
    IFormFieldDesignQuantities,
    IQuantityField,
    IFormFieldMaterialComposite,
    IFormFieldMultiSelector,
    IFormFieldAdditionalDataKey,
    IDimensionField,
    IFormFieldPortfolioData,
    FormFieldPortfolioDataKey,
    IFormFieldRadioList,
    IFormFieldShapeMaterial,
    IFormFieldProjectInformation,
    IFormFieldUploadFiles,
    IFormFieldBudget,
    IFormFieldComment,
} from '../interfaces/Form';
import { validateEmail, validatePhoneNumber } from './Auth';

export function isUndefined(value: any): boolean {
    return value === undefined;
};

export function isNotInOptions<T>(value: T | undefined | null, options: T[]): boolean {
    return !!(
        value !== undefined
        && value !== null
        && !!options
        && !options.includes(value)
    );
};

export function isOutOfRange(value: number | undefined | null, min: number, max: number): boolean {
    return !!(
        value !== undefined
        && value !== null
        && max !== undefined
        && min !== undefined
        && (value > max || value < min)
    );
};

export function isRequiredAndNull(value: any, required: boolean): boolean {
    return !!(
        value === null
        && required
    );
}

export function validateRequestEstimate({
    priceHT,
    priceMinHT,
    priceMaxHT,
    taxExonerated,
    taxRate,
    shippingIncluded,
    shippingPrice,
    shippingDelay,
}: IRequestEstimate): boolean {
    return !!(
        (priceHT || (priceMinHT && priceMaxHT))
        && (taxExonerated || taxRate)
        && (shippingIncluded || shippingPrice)
        && shippingDelay
    );
}

export function validateRequestQuote({
    priceHT,
    attachments,
}: IRequestQuote): boolean {
    return !!(
        priceHT
        && attachments
        && attachments.length === 1
    );
}

export function validateFormFieldContact(formField: IFormFieldContact): boolean {
    return !Object.entries(formField)
        .some(([key, { value, required, options }]) => (
            isUndefined(value)
            || isRequiredAndNull(value, required)
            || isNotInOptions(value, options)
            || (key === IFormFieldContactKey.Email && !validateEmail(value))
            || (key === IFormFieldContactKey.Phone && !validatePhoneNumber(value))
        ));
}

export function validateFormFieldDimension(formField: IFormFieldDimension): boolean {
    return !Object.entries(formField)
        .filter((v): v is [FieldDimensionId, IDimensionField] => v[1] !== undefined && !!Object.values<string>(FieldDimensionId).includes(v[0]))
        .some(([_, { value, required, range }]) => (
            isUndefined(value)
            || isOutOfRange(value, range.min, range.max)
            || isRequiredAndNull(value, required)
        ));
};

export function validateFormFieldComment(formField: IFormFieldComment): boolean {
    return !Object.entries(formField)
        .some(([_, {
            required, value,
        }]) => (
            isUndefined(value)
            || isRequiredAndNull(value, required)
        ));
};

export function validateFormFieldAdditionalData(formField: IFormFieldAdditionalData): boolean {
    return !Object.entries(formField)
        .some(([key, {
            required, options, value, max, min, range,
        }]) => (
            isUndefined(value)
            || isRequiredAndNull(value, required)
            || isNotInOptions(value, options)
            || (key === IFormFieldAdditionalDataKey.Quantity && isOutOfRange(value, min, max))
            || (key === IFormFieldAdditionalDataKey.Budget && isOutOfRange(value, range.min * (formField.quantity?.value || 1), range.max * (formField.quantity?.value || 1)))
        ));
};

function validateFormFieldUploadFiles({ required, value }: IFormFieldUploadFiles): boolean {
    return !(
        isUndefined(value)
        || isRequiredAndNull(value, required)
    );
};

export function validateFormFieldMaterial({ material: { required, value, options } }: IFormFieldMaterial): boolean {
    return !(
        isUndefined(value)
        || isRequiredAndNull(value, required)
        || (value !== MaterialId.Other && isNotInOptions(value, options))
    );
}

function validateFormFieldProjectInformation(formField: IFormFieldProjectInformation): boolean {
    return !Object.entries(formField)
        .some(([_, {
            required, options, value,
        }]) => (
            isUndefined(value)
            || isRequiredAndNull(value, required)
            || isNotInOptions(value, options)

        ));
};

export function validateFormFieldShape({ value, required, options }: IFormFieldShape): boolean {
    return !(
        isUndefined(value)
        || isRequiredAndNull(value, required)
        || isNotInOptions(value, options)
    );
}

export function validateFormFieldBudget({ value, required }: IFormFieldBudget): boolean {
    return !(
        isUndefined(value)
        || isRequiredAndNull(value, required)
    );
}

export function validateFormFieldApplianceLevel({ value, required }: IFormFieldBudget): boolean {
    return !(
        isUndefined(value)
        || isRequiredAndNull(value, required)
    );
}

function validateFormFieldShapeMaterial(formField: IFormFieldShapeMaterial): boolean {
    return validateFormFieldShape(formField.shape) && validateFormFieldMaterial(formField.material);
}

function validateFormFieldMaterialComposite(formField: IFormFieldMaterialComposite): boolean {
    return validateFormFieldMaterial(formField.materialPrimary) && validateFormFieldMaterial(formField.materialSecondary);
}

function validateFormFieldBoolean(formField: IFormFieldBoolean): boolean {
    return !(
        isUndefined(formField.value)
    );
}

export function validateFormFieldMultiSelector(formField: IFormFieldMultiSelector): boolean {
    return (
        formField.value !== undefined
        && (
            formField.value === null
                ? !formField.required
                : formField.value.every((value) => formField.options.includes(value))
        )
    );
};

export function validateFormFieldRadioList({ value, options }: IFormFieldRadioList): boolean {
    return !(
        isUndefined(value)
        || isNotInOptions(value, options)
    );
};

function validateFormFieldShapeQuantities(formField: IFormFieldShapeQuantities): boolean {
    return !Object.values(formField).some((field) => field?.value === undefined || (field.value < field.min && field.value > field.max));
}

export function validateFormFieldDesignQuantities(formField: IFormFieldDesignQuantities): boolean {
    // @ts-ignore
    const quantityFields = Object.values(formField).reduce((acc, curr) => [...acc, ...Object.values(curr)], ([] as IQuantityField[]));

    return !!quantityFields
        // @ts-ignore
        && quantityFields.some((quantity) => quantity?.value !== undefined && quantity.value > 0)
        // @ts-ignore
        && !quantityFields.some((quantity) => quantity?.value === undefined || (quantity.value < quantity.min && quantity.value > quantity.max));
}

export function validateFormFieldShapeDimension(formField: IFormFieldShapeDimension): boolean {
    return (
        formField.shape.value !== undefined
        && (formField.shape.value === null
            ? !formField.shape.required
            : formField.shape.options.some((option) => option === formField.shape.value)
                && validateFormFieldDimension(getFormFieldDimensionFromShapeDimension(formField))
        )
    );
}

export function validateFormFieldDescription(formField: IFormFieldDescription): boolean {
    return !!formField.value && formField.value.length > 0;
}

export function validateFormFieldPortfolioData(formField: IFormFieldPortfolioData): boolean {
    return !Object.entries(formField)
        .some(([key, { required, value, ...restField }]) => (
            isUndefined(value)
            || isRequiredAndNull(value, required)
            || (key === FormFieldPortfolioDataKey.Images && isOutOfRange(value.length, restField.min, restField.max))
            || (key === FormFieldPortfolioDataKey.Price && (Number.isNaN(value) || value < 0))
        ));
}

export const formFieldKeysToValidations = Object.freeze({
    [IFormFieldKey.PortfolioData]              : validateFormFieldPortfolioData,
    [IFormFieldKey.Comment]                    : validateFormFieldComment,
    [IFormFieldKey.AdditionalData]             : validateFormFieldAdditionalData,
    [IFormFieldKey.ApplianceLevel]             : validateFormFieldApplianceLevel,
    [IFormFieldKey.ProjectInformation]         : validateFormFieldProjectInformation,
    [IFormFieldKey.AddOns]                     : validateFormFieldShapeQuantities,
    [IFormFieldKey.Background]                 : validateFormFieldBoolean,
    [IFormFieldKey.BedShapeDimension]          : validateFormFieldShapeDimension,
    [IFormFieldKey.Budget]                     : validateFormFieldBudget,
    [IFormFieldKey.Border]                     : validateFormFieldShape,
    [IFormFieldKey.ClaustraDestination]        : validateFormFieldShape,
    [IFormFieldKey.ClaustraMobility]           : validateFormFieldShape,
    [IFormFieldKey.ClaustraType]               : validateFormFieldShape,
    [IFormFieldKey.ClaustraLaying]             : validateFormFieldBoolean,
    [IFormFieldKey.ClosetDoorFrameMaterial]    : validateFormFieldMaterial,
    [IFormFieldKey.ClosetDoorSens]             : validateFormFieldRadioList,
    [IFormFieldKey.ClosingType]                : validateFormFieldShape,
    [IFormFieldKey.Connectics]                 : validateFormFieldShapeQuantities,
    [IFormFieldKey.Constraints]                : validateFormFieldMultiSelector,
    [IFormFieldKey.Contact]                    : validateFormFieldContact,
    [IFormFieldKey.Cutting]                    : validateFormFieldShapeQuantities,
    [IFormFieldKey.DeckBoardShape]             : validateFormFieldShape,
    [IFormFieldKey.DeckBoardWidth]             : validateFormFieldRadioList,
    [IFormFieldKey.Description]                : validateFormFieldDescription,
    [IFormFieldKey.DesignProject]              : validateFormFieldDesignQuantities,
    [IFormFieldKey.DestinationShape]           : validateFormFieldShape,
    [IFormFieldKey.Dimension]                  : validateFormFieldDimension,
    [IFormFieldKey.Extension]                  : validateFormFieldShapeDimension,
    [IFormFieldKey.FenceDestination]           : validateFormFieldShape,
    [IFormFieldKey.FenceFilling]               : validateFormFieldShape,
    [IFormFieldKey.FenceGateDimension]         : validateFormFieldDimension,
    [IFormFieldKey.FenceMaterial]              : validateFormFieldMaterial,
    [IFormFieldKey.FencePoles]                 : validateFormFieldShapeMaterial,
    [IFormFieldKey.FenceProtection]            : validateFormFieldBoolean,
    [IFormFieldKey.FenceSecondMaterial]        : validateFormFieldMaterial,
    [IFormFieldKey.FenceRailing]               : validateFormFieldMaterial,
    [IFormFieldKey.FencingDestination]         : validateFormFieldMultiSelector,
    [IFormFieldKey.FencingPose]                : validateFormFieldRadioList,
    [IFormFieldKey.Finishing]                  : validateFormFieldRadioList,
    [IFormFieldKey.FrameMaterial]              : validateFormFieldMaterial,
    [IFormFieldKey.FrameShapeDimension]        : validateFormFieldShapeDimension,
    [IFormFieldKey.GarageDoorGate]             : validateFormFieldBoolean,
    [IFormFieldKey.GarageDoorGroove]           : validateFormFieldShape,
    [IFormFieldKey.GarageDoorOpening]          : validateFormFieldShape,
    [IFormFieldKey.GarageDoorOptions]          : validateFormFieldMultiSelector,
    [IFormFieldKey.GlassWallStyle]             : validateFormFieldShape,
    [IFormFieldKey.GroundShapeDimension]       : validateFormFieldShapeDimension,
    [IFormFieldKey.HighWindDestination]        : validateFormFieldShape,
    [IFormFieldKey.HighWindLaying]             : validateFormFieldBoolean,
    [IFormFieldKey.KitchenFurniture]           : validateFormFieldShapeQuantities,
    [IFormFieldKey.KitchenHandle]              : validateFormFieldShape,
    [IFormFieldKey.KitchenHomeAppliance]       : validateFormFieldShapeQuantities,
    [IFormFieldKey.KitchenLayout]              : validateFormFieldShape,
    [IFormFieldKey.KitchenProjectType]         : validateFormFieldRadioList,
    [IFormFieldKey.KitchenMaterialShade]       : validateFormFieldShape,
    [IFormFieldKey.KitchenShape]               : validateFormFieldShapeDimension,
    [IFormFieldKey.KitchenStyle]               : validateFormFieldShape,
    [IFormFieldKey.LegsDimension]              : validateFormFieldDimension,
    [IFormFieldKey.LegsMaterial]               : validateFormFieldMaterial,
    [IFormFieldKey.LegsShape]                  : validateFormFieldShape,
    [IFormFieldKey.LimonShape]                 : validateFormFieldShape,
    [IFormFieldKey.Material]                   : validateFormFieldMaterial,
    [IFormFieldKey.MirrorFrameMaterial]        : validateFormFieldMaterial,
    [IFormFieldKey.MultiSelector]              : validateFormFieldMultiSelector,
    [IFormFieldKey.Obstacles]                  : validateFormFieldShapeQuantities,
    [IFormFieldKey.Openings]                   : validateFormFieldShapeQuantities,
    [IFormFieldKey.PergolaOptions]             : validateFormFieldShapeQuantities,
    [IFormFieldKey.PergolaRoof]                : validateFormFieldRadioList,
    [IFormFieldKey.PergolaShape]               : validateFormFieldShape,
    [IFormFieldKey.PlainSteps]                 : validateFormFieldBoolean,
    [IFormFieldKey.PortalGate]                 : validateFormFieldDimension,
    [IFormFieldKey.PortalFilling]              : validateFormFieldShape,
    [IFormFieldKey.PortalLaying]               : validateFormFieldBoolean,
    [IFormFieldKey.PortalMotorization]         : validateFormFieldBoolean,
    [IFormFieldKey.PortalOpening]              : validateFormFieldShape,
    [IFormFieldKey.PortalShape]                : validateFormFieldShape,
    [IFormFieldKey.PortalShapeDimension]       : validateFormFieldShapeDimension,
    [IFormFieldKey.Quantity]                   : validateFormFieldShapeQuantities,
    [IFormFieldKey.SecondMaterial]             : validateFormFieldMaterial,
    [IFormFieldKey.RoofShapeDimension]         : validateFormFieldShapeDimension,
    [IFormFieldKey.Shape]                      : validateFormFieldShape,
    [IFormFieldKey.ShapeDimension]             : validateFormFieldShapeDimension,
    [IFormFieldKey.ShoesQuantity]              : validateFormFieldShapeQuantities,
    [IFormFieldKey.StaircaseGuard]             : validateFormFieldMaterialComposite,
    [IFormFieldKey.StaircaseLanding]           : validateFormFieldBoolean,
    [IFormFieldKey.StaircaseLayingStairs]      : validateFormFieldBoolean,
    [IFormFieldKey.StaircaseProtection]        : validateFormFieldBoolean,
    [IFormFieldKey.StaircaseRailing]           : validateFormFieldMaterial,
    [IFormFieldKey.StaircaseSideTurn]          : validateFormFieldShape,
    [IFormFieldKey.StepsDimension]             : validateFormFieldDimension,
    [IFormFieldKey.TerraceLaying]              : validateFormFieldBoolean,
    [IFormFieldKey.TerraceLight]               : validateFormFieldBoolean,
    [IFormFieldKey.TerracePedestals]           : validateFormFieldBoolean,
    [IFormFieldKey.TopBedShapeDimension]       : validateFormFieldShapeDimension,
    [IFormFieldKey.TrayDimension]              : validateFormFieldDimension,
    [IFormFieldKey.TrayMaterial]               : validateFormFieldMaterial,
    [IFormFieldKey.TrayShape]                  : validateFormFieldShape,
    [IFormFieldKey.TrayShapeDimension]         : validateFormFieldShapeDimension,
    [IFormFieldKey.Type]                       : validateFormFieldShape,
    [IFormFieldKey.UploadFiles]                : validateFormFieldUploadFiles,
    [IFormFieldKey.Vegetals]                   : validateFormFieldShapeQuantities,
    [IFormFieldKey.WindowBlindsDimension]      : validateFormFieldDimension,
    [IFormFieldKey.WindowBlindsDisposition]    : validateFormFieldShape,
    [IFormFieldKey.WindowBlindsDwelling]       : validateFormFieldShape,
    [IFormFieldKey.WindowBlindsFilling]        : validateFormFieldRadioList,
    [IFormFieldKey.WindowBlindsFunction]       : validateFormFieldShape,
    [IFormFieldKey.WindowBlindsElectric]       : validateFormFieldBoolean,
    [IFormFieldKey.WindowBlindsGlassDimension] : validateFormFieldDimension,
    [IFormFieldKey.WindowBlindsLight]          : validateFormFieldBoolean,
    [IFormFieldKey.WindowBlindsManeuver]       : validateFormFieldRadioList,
    [IFormFieldKey.WindowBlindsPose]           : validateFormFieldRadioList,
    [IFormFieldKey.WindowBlindsProtection]     : validateFormFieldRadioList,
    [IFormFieldKey.WindowBlindsStyle]          : validateFormFieldShape,
    [IFormFieldKey.WindowGlazing]              : validateFormFieldRadioList,
    [IFormFieldKey.WindowInstallation]         : validateFormFieldBoolean,
    [IFormFieldKey.WindowInsulation]           : validateFormFieldRadioList,
    [IFormFieldKey.WindowLeaf]                 : validateFormFieldShape,
    [IFormFieldKey.WindowMaterial]             : validateFormFieldMaterial,
    [IFormFieldKey.WindowOpening]              : validateFormFieldRadioList,
    [IFormFieldKey.WineCellarDegrees]          : validateFormFieldDimension,
    [IFormFieldKey.WineCellarDoor]             : validateFormFieldShape,
    [IFormFieldKey.WineCellarHumidity]         : validateFormFieldDimension,
    [IFormFieldKey.WineCellarLightingExpose]   : validateFormFieldBoolean,
    [IFormFieldKey.WineCellarMaterial]         : validateFormFieldMaterial,
    [IFormFieldKey.WineCellarNbBottle]         : validateFormFieldShapeQuantities,
    [IFormFieldKey.WineCellarNbDoor]           : validateFormFieldShapeQuantities,
    [IFormFieldKey.WineCellarPose]             : validateFormFieldShape,
    [IFormFieldKey.WineCellarRoom]             : validateFormFieldShape,
    [IFormFieldKey.WineCellarSensDoor]         : validateFormFieldShapeQuantities,
    [IFormFieldKey.WineCellarShape]            : validateFormFieldShape,
    [IFormFieldKey.WineCellarShelf]            : validateFormFieldShapeQuantities,
    [IFormFieldKey.WineCellarShelfMaterial]    : validateFormFieldMaterial,
    [IFormFieldKey.WineCellarSquareMeter]      : validateFormFieldDimension,
});
